/**
 * Reactive seed/merge of a member's per-user mirror(s) for shared SG
 * decks. Shared by web (per-deck, on the SG deck route) and mobile
 * (group-level, pre-seeding the whole group's deck list) so the seeding
 * STRATEGY can't drift between platforms — both run the same
 * `seedOrMergeSGMirror` whenever the SG shared cards change.
 *
 * A mirror is minted, and shared cards are merged into it, only from a
 * COMPLETE shared set — a deck the SG store marks loaded (`loadedDeckIds`).
 * The sync-init reply is a stream, so a deck's bucket is partial until its
 * completion frame; a mint from it would be a mirror with half the cards (the
 * old "member sees 0 cards on mobile" bug was this, from an empty first
 * pass). And only once this session's UserDo diff has landed
 * (`internal/personal-sync-ready.ts`), so a mirror minted on another device is
 * found rather than twinned. Under `lazySgCards` a deck the client has never
 * loaded arrives as properties alone: the hooks REQUEST its cards only when a mirror has to be
 * minted (`useHydrateSgDeckCards`), and an existing mirror is never a reason to
 * pull the shared set — the server's cross-DO fan-out keeps its shared fields
 * fresh. The repoint / re-link of a stale mirror needs no cards and runs
 * regardless.
 *
 * The merge overlays the member's personal fields from the mirror's LOCAL
 * rows, so it is only safe once the mirror's bucket is its FULL set
 * (`deckCardsMergeSafe`). Under lazy card hydration the rows sit on disk until
 * read: the merging hooks first bring every mirror's rows in from this
 * device's storage (`useHydrateDeckCardsFromDisk` — never the server, never
 * an activity bump). A disk read shorter than the deck is not enough; the
 * merge waits until the mirror is resident by any path (a deck open's server
 * stream, say), and re-runs then: each mirror's merge-safety is an effect
 * dependency (`useMergeSafeKey`). Until then the guarded merge skips the deck.
 */
import type { SyncGroupMembership } from '@flashka/shared/sync-engine/schema/entities';
import type { SGDeckProps } from '@flashka/shared/sync-engine/schema/group-entities';

import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { pickRecord } from '../derive-cache';
import { deckCardsMergeSafe } from '../internal/deck-residency';
import { usePersonalSyncReady } from '../internal/personal-sync-ready';
import { findLocalDeckIdForSGDeck } from '../internal/sg-mirror-resolver';
import { useFlashkaStore } from '../internal/store/flashka-store';
import { useStudyGroupStore } from '../internal/store/study-group-store';
import { useSyncOptional } from '../provider/sync-core';
import { sync } from '../sync';
import { useHydrateDeckCardsFromDisk } from '../use-hydrate-deck-cards-from-disk';
import { seedOrMergeSGMirror, sgKey } from './mirror-seed';
import { useHydrateSgDeckCards } from './sg-deck-cards-load';

const NO_IDS: readonly string[] = [];
const NO_LOADED: ReadonlySet<string> = new Set();

/**
 * Split the group's live decks into the ids of their existing mirrors (sorted
 * — what the disk loader needs) and the SG deck ids that have NO mirror yet
 * (in deck order — what the on-demand card loader needs). One resolver pass
 * over the properties map — which a card write never replaces — selected as a
 * flat, shallow-compared list (`[mirrorCount, ...mirrorIds, ...unmintedIds]`)
 * so a card frame re-runs neither loader and a properties change that moves
 * no mirror keeps both arrays' identity.
 */
function useMirrorSplit(
  groupId: string,
  sgDecks: readonly SGDeckProps[],
): { mirrorIds: readonly string[]; unmintedSgDeckIds: readonly string[] } {
  const flat = sync.decks.useProperties(
    useShallow((all) => {
      const mirrors: string[] = [];
      const unminted: string[] = [];
      for (const sgDeck of sgDecks) {
        if (sgDeck.deletedTimeISO) continue;
        const id = findLocalDeckIdForSGDeck(
          all,
          groupId,
          sgDeck.id,
          sgKey(groupId, sgDeck.deckName),
        );
        if (id) mirrors.push(id);
        else unminted.push(sgDeck.id);
      }
      return [String(mirrors.length), ...mirrors.sort(), ...unminted];
    }),
  );
  return useMemo(() => {
    const mirrorCount = Number(flat[0]);
    return {
      mirrorIds: mirrorCount > 0 ? flat.slice(1, 1 + mirrorCount) : NO_IDS,
      unmintedSgDeckIds:
        flat.length > 1 + mirrorCount ? flat.slice(1 + mirrorCount) : NO_IDS,
    };
  }, [flat]);
}

/**
 * The mirrors among `mirrorIds` the guarded merge may write into right now,
 * as a string, so the effect re-runs the moment one becomes resident (a deck
 * open's stream lands, say) and never on a card frame that changes nothing.
 */
function useMergeSafeKey(mirrorIds: readonly string[]): string {
  return useFlashkaStore((s) =>
    mirrorIds.filter((id) => deckCardsMergeSafe(s, id)).join(','),
  );
}

function useLoadedDeckIds(groupId: string): ReadonlySet<string> {
  return useStudyGroupStore((s) => s.groups[groupId]?.loadedDeckIds ?? NO_LOADED);
}

/**
 * The mint gate is shut and nothing is about to open it: only this session's
 * UserDo handshake confirms the memberships, and it is not under way — it
 * failed, or its socket is down. A gated deck is then unavailable, not
 * minting; without this an offline cold start span on it forever. The provider
 * STARTS at `disconnected` before it is asked to connect (web holds it there
 * through disk hydration), so that status counts only once the socket was seen
 * trying (here, or by a `ready` this session) or the device is offline — or it
 * would flash the unavailable state.
 */
function useMintGateStalled(canMint: boolean): boolean {
  const userDo = useSyncOptional();
  const status = userDo?.syncStatus;
  const [sawLive, setSawLive] = useState(false);
  useEffect(() => {
    if (status && status !== 'disconnected' && status !== 'error') setSawLive(true);
  }, [status]);
  if (canMint || !userDo) return false;
  if (status === 'error') return true;
  return (
    status === 'disconnected' &&
    (sawLive || userDo.hasCompletedInitialSync || !userDo.isOnline)
  );
}

/**
 * Seed/merge the mirror for a single SG deck. Use on a per-deck surface
 * (e.g. the web SG deck route). Re-runs whenever the SG deck's shared
 * props or cards change. Requests the deck's cards when it has no mirror
 * yet and the group syncs lazily; mints and merges only once the deck is
 * loaded.
 */
export function useSeedOrMergeSGMirror(
  groupId: string,
  sgDeckId: string,
): SgMirrorSeedState {
  const scoped = sync.studyGroups.group(groupId).decks.deck(sgDeckId);
  const props = scoped.useProperties();
  const sharedCards = scoped.useCards();
  const viewerUserId = useFlashkaStore((s) => s.currentUserId);
  const deckName = props?.deckName;
  const mirrorIds = sync.decks.useProperties(
    useShallow((all) => {
      if (!deckName) return NO_IDS;
      const id = findLocalDeckIdForSGDeck(
        all,
        groupId,
        sgDeckId,
        sgKey(groupId, deckName),
      );
      return id ? [id] : NO_IDS;
    }),
  );
  const hasMirror = mirrorIds.length > 0;
  const loaded = useLoadedDeckIds(groupId).has(sgDeckId);
  const { isLoading } = useHydrateDeckCardsFromDisk(mirrorIds);
  const mergeSafeKey = useMergeSafeKey(mirrorIds);
  const personalReady = usePersonalSyncReady();
  // Only a deck without a mirror needs the shared set pulled — for the mint.
  const mintable = !hasMirror && !!props && !props.deletedTimeISO;
  // …and only for a confirmed live membership (`canMintGroupMirrors`): this
  // route stays mounted over a deleted group's cached deck, and deleting the
  // viewer's own original here minted the deck straight back.
  const canMint = useFlashkaStore((s) =>
    canMintGroupMirrors(s as unknown as MintGateState, groupId),
  );
  const mintStalled = useMintGateStalled(canMint);
  // Memoised so the hydrate hook below (and this hook's own return) do not see
  // a fresh array on every render of the deck route. Requested whether or not
  // the gate is open — the gate holds the MINT — so an offline group socket
  // still ends the wait as `unavailable`.
  const wanted = useMemo(() => (mintable ? [sgDeckId] : NO_IDS), [mintable, sgDeckId]);
  const { isLoading: sgLoading, unavailable: sgUnavailable } = useHydrateSgDeckCards(
    groupId,
    wanted,
  );

  useEffect(() => {
    if (!props || props.deletedTimeISO) return;
    // Nothing to mint from until the shared set is complete — or the gate opens.
    if (!hasMirror && (!loaded || !canMint)) return;
    seedOrMergeSGMirror({
      groupId,
      sgDeckId,
      deckName: props.deckName,
      cards: loaded ? sharedCards : [],
      viewerUserId,
      ownerUserId: props.addedByUserId,
      displayName: props.displayName,
      color: props.color,
      editPermission: props.editPermission ?? 'owner-only',
      // An existing mirror of an unloaded deck: repoint / re-link only.
      mergeCards: loaded,
    });
    // `isLoading` re-runs the guarded merge once the mirror's rows are in,
    // `mergeSafeKey` once the mirror is resident, `sgLoading` once the shared
    // set is complete, `personalReady` a mint that waited for the UserDo diff.
  }, [
    groupId,
    sgDeckId,
    props,
    sharedCards,
    viewerUserId,
    hasMirror,
    loaded,
    isLoading,
    mergeSafeKey,
    sgLoading,
    canMint,
    personalReady,
  ]);

  // The same three answers the group-level hook gives, for the one deck this
  // surface is about: the web SG deck route blocks on the mirror existing, and
  // without an end to the wait it span forever on a deck whose shared set
  // could not be fetched.
  return useMemo(
    () => ({
      unmintedSgDeckIds: wanted,
      isMinting: mintable && !sgUnavailable && !mintStalled,
      unavailable: mintable && (sgUnavailable || mintStalled),
    }),
    [wanted, mintable, sgUnavailable, mintStalled],
  );
}

/**
 * Seed/merge mirrors for EVERY deck in a group. Use on a group-level
 * surface (e.g. the mobile study-group deck list). Re-runs whenever the
 * deck list OR any deck's shared cards change — `groupCards` in the dep
 * array is what lets a late card arrival heal a previously-empty seed.
 * Requests the shared cards of every deck WITHOUT a mirror (the mint needs
 * them); an existing mirror's deck is merged only if it happens to be loaded.
 */
export function useSeedOrMergeSGMirrorsForGroup(groupId: string): SgMirrorSeedState {
  return useSeedOrMergeSGMirrorsForDecks(
    groupId,
    sync.studyGroups.group(groupId).decks.use(),
  );
}

/**
 * What a surface that LISTS a group's decks needs from the seeding, because
 * every mirror-keyed reader drops a deck whose mirror does not exist yet.
 *
 * Without this the group screen and the SG folder runners answered "no study
 * sets yet" / "folder not found" for a group that has decks — a first open, or
 * any open offline. `isMinting` is the wait; `unavailable` is the honest end
 * of it when the shared sets cannot be fetched at all.
 */
export interface SgMirrorSeedState {
  /** Group decks with no mirror on this device yet. */
  unmintedSgDeckIds: readonly string[];
  /** At least one is un-minted and the shared set is still coming. */
  isMinting: boolean;
  /** At least one is un-minted and nothing is still trying. */
  unavailable: boolean;
}

/**
 * The same seed/merge, for a NAMED SUBSET of the group's decks — a folder's
 * decks, say. Prefer it wherever the surface knows which decks it is about:
 * the whole-group version re-runs `mergeCardsLWW` plus a per-card storage
 * write for EVERY deck in the group whenever ANY of the group's cards change,
 * and a surface mounted by a study runner pays that for decks it never shows.
 * Both the shared-card subscription and the card requests are scoped to the
 * decks passed in, so a frame for a deck outside the set re-runs nothing.
 */
export function useSeedOrMergeSGMirrorsForDecks(
  groupId: string,
  sgDecks: readonly SGDeckProps[],
): SgMirrorSeedState {
  const sgDeckIds = useMemo(() => sgDecks.map((d) => d.id), [sgDecks]);
  const groupCards = useStudyGroupStore(
    useShallow((s) => pickRecord(s.groups[groupId]?.cards ?? {}, sgDeckIds)),
  );
  const loadedDeckIds = useLoadedDeckIds(groupId);
  const viewerUserId = useFlashkaStore((s) => s.currentUserId);
  const { mirrorIds, unmintedSgDeckIds } = useMirrorSplit(groupId, sgDecks);
  const { isLoading } = useHydrateDeckCardsFromDisk(mirrorIds);
  const mergeSafeKey = useMergeSafeKey(mirrorIds);
  const personalReady = usePersonalSyncReady();
  // Same gate as the root hook: mint only for a confirmed live membership. It
  // holds the mint, not the request, so an offline group still ends the wait.
  const canMint = useFlashkaStore((s) =>
    canMintGroupMirrors(s as unknown as MintGateState, groupId),
  );
  const mintStalled = useMintGateStalled(canMint);
  const { isLoading: sgLoading, unavailable: sgUnavailable } = useHydrateSgDeckCards(
    groupId,
    unmintedSgDeckIds,
  );

  useEffect(() => {
    const unminted = new Set(unmintedSgDeckIds);
    for (const sgDeck of sgDecks) {
      if (sgDeck.deletedTimeISO) continue;
      const loaded = loadedDeckIds.has(sgDeck.id);
      // No mirror and no complete shared set (or no confirmed membership):
      // nothing to mint from yet.
      if (unminted.has(sgDeck.id) && (!loaded || !canMint)) continue;
      seedOrMergeSGMirror({
        groupId,
        sgDeckId: sgDeck.id,
        deckName: sgDeck.deckName,
        cards: loaded ? Object.values(groupCards?.[sgDeck.id] ?? {}) : [],
        viewerUserId,
        ownerUserId: sgDeck.addedByUserId,
        displayName: sgDeck.displayName,
        color: sgDeck.color,
        editPermission: sgDeck.editPermission ?? 'owner-only',
        // An existing mirror of an unloaded deck: repoint / re-link only.
        mergeCards: loaded,
      });
    }
    // `isLoading` re-runs the guarded merge once the mirrors' rows are in,
    // `mergeSafeKey` once a mirror is resident, `sgLoading` once a requested
    // shared set is complete, `personalReady` a mint that waited for the
    // UserDo diff.
  }, [
    groupId,
    sgDecks,
    groupCards,
    loadedDeckIds,
    unmintedSgDeckIds,
    viewerUserId,
    isLoading,
    mergeSafeKey,
    sgLoading,
    canMint,
    personalReady,
  ]);

  return useMemo(
    () => ({
      unmintedSgDeckIds,
      isMinting: unmintedSgDeckIds.length > 0 && !sgUnavailable && !mintStalled,
      unavailable: unmintedSgDeckIds.length > 0 && (sgUnavailable || mintStalled),
    }),
    [unmintedSgDeckIds, sgUnavailable, mintStalled],
  );
}

/**
 * IDENTITY-ONLY seeding for EVERY deck in a group — the root orchestrator's
 * hook. Mints a mirror the first time a member's client learns of an SG deck
 * (with its complete shared set — requested on demand under `lazySgCards`),
 * and repoints / re-links a stale mirror after a delete→re-add — the two
 * things that must keep working without a reload while the member is on an
 * unrelated screen. A new shared deck therefore shows up on the Home group
 * tile, with its count, without the member opening the group.
 *
 * What it deliberately does NOT do: merge shared cards into an EXISTING
 * mirror. That is the surfaces' job (`useSeedOrMergeSGMirrorsForGroup` on the
 * mobile group deck list, `useSeedOrMergeSGMirror` on the web SG deck route),
 * and it is why `groupCards` is NOT a dependency here: at root the old hook
 * re-merged every deck of every group on every group card frame — a full
 * `mergeCardsLWW` + store tick + per-card SQLite upsert per deck, ~20k card
 * encodes per pass for a member of two large groups, all of it landing while
 * the Home grid was trying to paint. Shared-field edits by other members still
 * reach existing mirrors through the server's cross-DO fan-out
 * (`apply-shared-field-from-group`), so nothing user-visible depends on the
 * root merge. `loadedDeckIds` IS a dependency: it changes on completion frames
 * and at hydrate, not per frame, and it is what turns a pending mint into one.
 */
type MintGateState = {
  membershipsConfirmed?: boolean;
  groupMemberships?: readonly SyncGroupMembership[];
};

/**
 * May the root hook mint mirrors for `groupId`? Only for a LIVE membership,
 * and only once this session's UserDo handshake has confirmed the
 * memberships: before that, a group deleted or left on another device still
 * reads as joined from the disk cache. A member who joins mid-session mints as
 * soon as the membership arrives — the confirmation is per session.
 */
export function canMintGroupMirrors(state: MintGateState, groupId: string): boolean {
  return (
    state.membershipsConfirmed === true &&
    (state.groupMemberships ?? []).some((m) => m.groupId === groupId && !m.deletedTimeISO)
  );
}

export function useEnsureSGMirrorsForGroup(groupId: string): void {
  const sgDecks = sync.studyGroups.group(groupId).decks.use();
  const viewerUserId = useFlashkaStore((s) => s.currentUserId);
  const loadedDeckIds = useLoadedDeckIds(groupId);
  const { unmintedSgDeckIds } = useMirrorSplit(groupId, sgDecks);
  // A mint waits for this session's UserDo diff (`seedOrMergeSGMirror`).
  const personalReady = usePersonalSyncReady();
  // NO CONFIRMED LIVE MEMBERSHIP, NO MINT. The orchestrator keeps this hook
  // mounted for the connection pool's grace period after a leave or a group
  // delete, and a reload hydrates the cached membership before sync-init
  // corrects it — both over the group's cached deck list. A deck the viewer
  // then deletes (their own shared original, or their mirror) has no live
  // local twin, so the resolver says "unmirrored" and this minted a fresh
  // `__sg_` copy of a group they are no longer in: the deck came back.
  // Existing mirrors are not touched — leaving or deleting a group removes
  // nothing from the library. See `canMintGroupMirrors`.
  const canMint = useFlashkaStore((s) =>
    canMintGroupMirrors(s as unknown as MintGateState, groupId),
  );
  // The mint needs the complete shared set; request it for the decks that have
  // no mirror yet (a no-op with the flag off — the sync-init stream loads them).
  useHydrateSgDeckCards(groupId, canMint ? unmintedSgDeckIds : NO_IDS);

  useEffect(() => {
    if (!canMint) return;
    // Read the cards lazily for the MINT path only — a fresh mirror has
    // nothing to lose, and a subscription here would re-run this effect on
    // every group card frame, which is exactly the cost this hook removes.
    const groupCards = useStudyGroupStore.getState().groups[groupId]?.cards;
    const unminted = new Set(unmintedSgDeckIds);
    for (const sgDeck of sgDecks) {
      if (sgDeck.deletedTimeISO) continue;
      const loaded = loadedDeckIds.has(sgDeck.id);
      // No mirror and no complete shared set: leave the mint for when it is.
      if (!loaded && unminted.has(sgDeck.id)) continue;
      seedOrMergeSGMirror({
        groupId,
        sgDeckId: sgDeck.id,
        deckName: sgDeck.deckName,
        cards: loaded ? Object.values(groupCards?.[sgDeck.id] ?? {}) : [],
        viewerUserId,
        ownerUserId: sgDeck.addedByUserId,
        displayName: sgDeck.displayName,
        color: sgDeck.color,
        editPermission: sgDeck.editPermission ?? 'owner-only',
        mergeCards: false,
      });
    }
  }, [
    groupId,
    sgDecks,
    viewerUserId,
    loadedDeckIds,
    unmintedSgDeckIds,
    canMint,
    personalReady,
  ]);
}
